import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// @ts-ignore
import "./BookingSection.css";
import { useEmailJS } from "../../../hooks/useEmailJS";
import { EMAIL_ADDRESS } from "../../../components/EmailAddress/EmailAddress";
import { trackGenerateLead } from "../../../analytics";
import {
  HOURLY_OPTIONS,
  LUGGAGE_OPTIONS,
  SERVICE_OPTIONS,
  STOP_OPTIONS,
  TRIP_TYPES,
  buildTripNotes,
} from "../../../data/quoteOptions";

interface FormState {
  tripType: string;
  pickup: string;
  dropoff: string;
  date: string;
  time: string;
  returnDate: string;
  returnTime: string;
  hours: string;
  flight: string;
  meetGreet: boolean;
  luggage: string;
  stops: string;
  vehicle: string;
  name: string;
  email: string;
  phone: string;
  service: string;
  passengers: string;
  notes: string;
}

type Errors = Partial<Record<keyof FormState, string>>;

const initialForm: FormState = {
  tripType: TRIP_TYPES[0],
  pickup: "",
  dropoff: "",
  date: "",
  time: "",
  returnDate: "",
  returnTime: "",
  hours: HOURLY_OPTIONS[0],
  flight: "",
  meetGreet: false,
  luggage: "",
  stops: "",
  vehicle: "",
  name: "",
  email: "",
  phone: "",
  service: "",
  passengers: "1",
  notes: "",
};

const VEHICLES = [
  {
    key: "LUXURY SEDAN",
    label: "Executive Sedan",
    capacity: "Seating capacity shared on request",
    img: `${process.env.PUBLIC_URL}/images/sedan-v2.webp`,
  },
  {
    key: "FULL SUV",
    label: "Full-Size Luxury SUV",
    capacity: "Seating capacity shared on request",
    img: `${process.env.PUBLIC_URL}/images/suv-v2.webp`,
  },
  {
    key: "SPRINTER",
    label: "Mercedes Sprinter",
    capacity: "Seating capacity shared on request",
    img: `${process.env.PUBLIC_URL}/images/benz-sprinter-v2.webp`,
  },
];

const STEP_LABELS = ["Trip Details", "Choose Vehicle", "Your Details"];

function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toTimeString(date: Date): string {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

export default function BookingSection(): JSX.Element {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Errors>({});
  const { status, send } = useEmailJS();
  const leadTracked = useRef(false);

  // react-datepicker state
  const [pickDate, setPickDate] = useState<Date | null>(null);
  const [pickTime, setPickTime] = useState<Date | null>(null);
  const [pickReturnDate, setPickReturnDate] = useState<Date | null>(null);
  const [pickReturnTime, setPickReturnTime] = useState<Date | null>(null);

  useEffect(() => {
    if (status === "sending") {
      leadTracked.current = false;
    }
    if (status === "success") {
      if (!leadTracked.current) {
        trackGenerateLead("quote");
        leadTracked.current = true;
      }
      setForm(initialForm);
      setStep(1);
      setErrors({});
      setPickDate(null);
      setPickTime(null);
      setPickReturnDate(null);
      setPickReturnTime(null);
    }
  }, [status]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ): void => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDateChange = (date: Date | null): void => {
    setPickDate(date);
    setForm((prev) => ({ ...prev, date: date ? toDateString(date) : "" }));
    setErrors((prev) => ({ ...prev, date: "" }));
  };

  const handleTimeChange = (time: Date | null): void => {
    setPickTime(time);
    setForm((prev) => ({ ...prev, time: time ? toTimeString(time) : "" }));
    setErrors((prev) => ({ ...prev, time: "" }));
  };

  const handleReturnDateChange = (date: Date | null): void => {
    setPickReturnDate(date);
    setForm((prev) => ({
      ...prev,
      returnDate: date ? toDateString(date) : "",
    }));
    setErrors((prev) => ({ ...prev, returnDate: "" }));
  };

  const handleReturnTimeChange = (time: Date | null): void => {
    setPickReturnTime(time);
    setForm((prev) => ({
      ...prev,
      returnTime: time ? toTimeString(time) : "",
    }));
    setErrors((prev) => ({ ...prev, returnTime: "" }));
  };

  const selectTripType = (tripType: string): void => {
    setForm((prev) => ({ ...prev, tripType }));
    setErrors({});
  };

  const validateStep1 = (): boolean => {
    const errs: Errors = {};
    if (!form.pickup.trim()) errs.pickup = "Pickup location is required";
    if (form.tripType !== "Hourly" && !form.dropoff.trim())
      errs.dropoff = "Drop-off location is required";
    if (!form.date) errs.date = "Please select a pickup date";
    if (!form.time) errs.time = "Please select a pickup time";
    if (form.tripType === "Round-trip") {
      if (!form.returnDate) errs.returnDate = "Please select a return date";
      if (!form.returnTime) errs.returnTime = "Please select a return time";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = (): boolean => {
    if (!form.vehicle) {
      setErrors({ vehicle: "Please select a vehicle to continue" });
      return false;
    }
    return true;
  };

  const validateStep3 = (): boolean => {
    const errs: Errors = {};
    if (!form.name.trim()) errs.name = "Full name is required";
    if (!form.phone.trim()) errs.phone = "Phone number is required";
    if (!form.email.trim()) errs.email = "Email address is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      errs.email = "Enter a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = (): void => {
    if (step === 1 && validateStep1()) {
      setStep(2);
      setErrors({});
    } else if (step === 2 && validateStep2()) {
      setStep(3);
      setErrors({});
    }
  };

  const goBack = (): void => {
    setStep((s) => s - 1);
    setErrors({});
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!validateStep3()) return;
    send({
      from_name: form.name,
      from_email: form.email,
      phone: form.phone,
      service:
        form.service ||
        (form.tripType === "Airport" ? "Airport Transfer" : "Point-to-Point"),
      vehicle: form.vehicle,
      pickup: form.pickup,
      dropoff: form.dropoff,
      date: form.date,
      time: form.time,
      passengers: form.passengers,
      trip_type: form.tripType,
      flight: form.flight,
      notes: buildTripNotes({
        tripType: form.tripType,
        returnDate: form.returnDate,
        returnTime: form.returnTime,
        hours: form.hours,
        flight: form.flight,
        luggage: form.luggage,
        stops: form.stops,
        meetGreet: form.meetGreet,
        notes: form.notes,
      }),
      to_email: EMAIL_ADDRESS,
    });
  };

  const selectedVehicle = VEHICLES.find((v) => v.key === form.vehicle);
  const formatDate = (value: string): string =>
    value
      ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";
  const formatTime = (value: string): string => {
    if (!value) return "";
    const [h, m] = value.split(":").map(Number);
    const d = new Date();
    d.setHours(h, m);
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  };
  const summaryDate = formatDate(form.date);

  return (
    <section className="booking-section py-5" id="booking">
      <div className="container">
        <div className="row align-items-center g-3">
          {/* ── Left panel ── */}
          <div className="col-lg-5 gs-anim">
            <span className="section-badge">Get a Quote</span>
            <h2 className="section-title">Get a Quote</h2>
            <p className="booking-lead">
              Experience luxury travel in 3 steps. Fill out the form and we'll go
              over your trip details, scheduling, and pricing before you finalize
              a reservation.
            </p>
            <ul className="booking-perks">
              <li>
                <i className="fas fa-check-circle"></i> Scheduling and pricing
                outlined before you book
              </li>
              <li>
                <i className="fas fa-check-circle"></i> Car choices matched to
                your group
              </li>
              <li>
                <i className="fas fa-check-circle"></i> Airport pickup details
                shared before travel
              </li>
              <li>
                <i className="fas fa-check-circle"></i> Reservation finalized once
                you agree to the details
              </li>
            </ul>
            <div className="booking-contact-cards">
              <a
                href="tel:9294526031"
                className="booking-contact-card"
                title="Call GS Limo Elite at (929) 452-6031"
              >
                <i className="fas fa-phone"></i>
                <div>
                  <small>Call Directly</small>
                  <strong>(929) 452-6031</strong>
                </div>
              </a>
              <a
                href={`mailto:${EMAIL_ADDRESS}`}
                className="booking-contact-card"
              >
                <i className="fas fa-envelope"></i>
                <div>
                  <small>Email Us</small>
                  <strong>Send a Message</strong>
                </div>
              </a>
            </div>
          </div>

          {/* ── Right panel ── */}
          <div className="col-lg-7 gs-anim">
            <div className="booking-form-card">
              {/* Step indicator */}
              <div className="bs-steps">
                {STEP_LABELS.map((label, i) => (
                  <div
                    key={i}
                    className={`bs-step${step === i + 1 ? " active" : ""}${step > i + 1 ? " done" : ""}`}
                  >
                    <div className="bs-step-bubble">
                      {step > i + 1 ? (
                        <i className="fas fa-check"></i>
                      ) : (
                        <span>{i + 1}</span>
                      )}
                    </div>
                    <p className="bs-step-label">{label}</p>
                    {i < STEP_LABELS.length - 1 && (
                      <div className="bs-step-line"></div>
                    )}
                  </div>
                ))}
              </div>

              {status === "success" && (
                <div className="booking-alert booking-alert-success">
                  <i className="fas fa-check-circle me-2"></i>
                  Thanks — your request is in. We’ll go over your trip details,
                  scheduling, and pricing with you before the reservation is
                  finalized.
                </div>
              )}
              {status === "error" && (
                <div className="booking-alert booking-alert-error">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {"Something went wrong. Please call "}
                  <a
                    href="tel:9294526031"
                    title="Call GS Limo Elite at (929) 452-6031"
                    style={{ color: "var(--gold)", fontWeight: 700 }}
                  >
                    (929) 452-6031
                  </a>
                  .
                </div>
              )}

              {/* ── Step 1: Trip Details ── */}
              {step === 1 && (
                <div className="bs-panel">
                  <h3 className="booking-form-title">
                    <i className="fas fa-route me-2"></i>Trip Details
                  </h3>

                  <div
                    className="bs-trip-types"
                    role="group"
                    aria-label="Trip type"
                  >
                    {TRIP_TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`bs-trip-type${form.tripType === t ? " active" : ""}`}
                        aria-pressed={form.tripType === t}
                        onClick={() => selectTripType(t)}
                      >
                        {t}
                      </button>
                    ))}
                  </div>

                  <div className="row g-3">
                    <div className="col-12">
                      <label className="gs-label" htmlFor="booking-pickup">
                        Pickup Location *
                      </label>
                      <div className="gs-icon-input">
                        <i className="fas fa-circle gs-icon-dot pickup-dot"></i>
                        <input
                          id="booking-pickup"
                          type="text"
                          name="pickup"
                          value={form.pickup}
                          onChange={handleChange}
                          className={`gs-input${errors.pickup ? " gs-input-err" : ""}`}
                          placeholder="Full address or airport name"
                        />
                      </div>
                      {errors.pickup && (
                        <span className="gs-err">{errors.pickup}</span>
                      )}
                    </div>

                    <div className="col-12">
                      <label className="gs-label" htmlFor="booking-dropoff">
                        {form.tripType === "Hourly"
                          ? "Destinations / Notes (optional)"
                          : "Drop-off Location *"}
                      </label>
                      <div className="gs-icon-input">
                        <i className="fas fa-map-marker-alt gs-icon-dot dropoff-dot"></i>
                        <input
                          id="booking-dropoff"
                          type="text"
                          name="dropoff"
                          value={form.dropoff}
                          onChange={handleChange}
                          className={`gs-input${errors.dropoff ? " gs-input-err" : ""}`}
                          placeholder={
                            form.tripType === "Hourly"
                              ? "Where will you be going?"
                              : "Destination address"
                          }
                        />
                      </div>
                      {errors.dropoff && (
                        <span className="gs-err">{errors.dropoff}</span>
                      )}
                    </div>

                    {/* Date picker */}
                    <div className="col-sm-6">
                      <label className="gs-label" htmlFor="booking-date">
                        <i className="fas fa-calendar-alt me-1 text-gold-dim"></i>
                        Pickup Date *
                      </label>
                      <div className="gs-dp-wrap">
                        <i className="fas fa-calendar-alt gs-dp-icon"></i>
                        <DatePicker
                          id="booking-date"
                          selected={pickDate}
                          onChange={handleDateChange}
                          minDate={new Date()}
                          placeholderText="Select a date"
                          dateFormat="MMM d, yyyy"
                          className={`gs-input gs-dp-input${errors.date ? " gs-input-err" : ""}`}
                          calendarClassName="gs-calendar"
                          popperPlacement="bottom-start"
                          autoComplete="off"
                        />
                      </div>
                      {errors.date && (
                        <span className="gs-err">{errors.date}</span>
                      )}
                    </div>

                    {/* Time picker */}
                    <div className="col-sm-6">
                      <label className="gs-label" htmlFor="booking-time">
                        <i className="fas fa-clock me-1 text-gold-dim"></i>
                        Pickup Time *
                      </label>
                      <div className="gs-dp-wrap">
                        <i className="fas fa-clock gs-dp-icon"></i>
                        <DatePicker
                          id="booking-time"
                          selected={pickTime}
                          onChange={handleTimeChange}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={30}
                          timeCaption="Time"
                          dateFormat="h:mm aa"
                          placeholderText="Select a time"
                          className={`gs-input gs-dp-input${errors.time ? " gs-input-err" : ""}`}
                          calendarClassName="gs-calendar gs-timepicker"
                          popperPlacement="bottom-start"
                        />
                      </div>
                      {errors.time && (
                        <span className="gs-err">{errors.time}</span>
                      )}
                    </div>

                    {form.tripType === "Round-trip" && (
                      <>
                        <div className="col-sm-6">
                          <label className="gs-label" htmlFor="booking-return-date">
                            <i className="fas fa-calendar-alt me-1 text-gold-dim"></i>
                            Return Date *
                          </label>
                          <div className="gs-dp-wrap">
                            <i className="fas fa-calendar-alt gs-dp-icon"></i>
                            <DatePicker
                              id="booking-return-date"
                              selected={pickReturnDate}
                              onChange={handleReturnDateChange}
                              minDate={pickDate ?? new Date()}
                              placeholderText="Select a date"
                              dateFormat="MMM d, yyyy"
                              className={`gs-input gs-dp-input${errors.returnDate ? " gs-input-err" : ""}`}
                              calendarClassName="gs-calendar"
                              popperPlacement="bottom-start"
                              autoComplete="off"
                            />
                          </div>
                          {errors.returnDate && (
                            <span className="gs-err">{errors.returnDate}</span>
                          )}
                        </div>
                        <div className="col-sm-6">
                          <label className="gs-label" htmlFor="booking-return-time">
                            <i className="fas fa-clock me-1 text-gold-dim"></i>
                            Return Time *
                          </label>
                          <div className="gs-dp-wrap">
                            <i className="fas fa-clock gs-dp-icon"></i>
                            <DatePicker
                              id="booking-return-time"
                              selected={pickReturnTime}
                              onChange={handleReturnTimeChange}
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={30}
                              timeCaption="Time"
                              dateFormat="h:mm aa"
                              placeholderText="Select a time"
                              className={`gs-input gs-dp-input${errors.returnTime ? " gs-input-err" : ""}`}
                              calendarClassName="gs-calendar gs-timepicker"
                              popperPlacement="bottom-start"
                            />
                          </div>
                          {errors.returnTime && (
                            <span className="gs-err">{errors.returnTime}</span>
                          )}
                        </div>
                      </>
                    )}

                    {form.tripType === "Hourly" && (
                      <div className="col-sm-6">
                        <label className="gs-label" htmlFor="booking-hours">
                          <i className="fas fa-hourglass-half me-1 text-gold-dim"></i>
                          Duration
                        </label>
                        <select
                          id="booking-hours"
                          name="hours"
                          value={form.hours}
                          onChange={handleChange}
                          className="gs-input"
                        >
                          {HOURLY_OPTIONS.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {form.tripType === "Airport" && (
                      <>
                        <div className="col-sm-6">
                          <label className="gs-label" htmlFor="booking-flight">
                            <i className="fas fa-plane me-1 text-gold-dim"></i>
                            Flight Number
                          </label>
                          <input
                            id="booking-flight"
                            type="text"
                            name="flight"
                            value={form.flight}
                            onChange={handleChange}
                            className="gs-input"
                            placeholder="e.g. AA 1234"
                          />
                        </div>
                        <div className="col-sm-6 d-flex align-items-end">
                          <label className="bs-check" htmlFor="booking-meet">
                            <input
                              id="booking-meet"
                              type="checkbox"
                              checked={form.meetGreet}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  meetGreet: e.target.checked,
                                }))
                              }
                            />
                            <span>Meet &amp; greet inside terminal</span>
                          </label>
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn gs-btn-gold w-100 bs-btn mt-4"
                    onClick={goNext}
                  >
                    Next: Vehicle
                    <i className="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              )}

              {/* ── Step 2: Vehicle ── */}
              {step === 2 && (
                <div className="bs-panel">
                  <h3 className="booking-form-title">
                    <i className="fas fa-car me-2"></i>Choose Your Vehicle
                  </h3>
                  <div className="bs-vehicle-grid">
                    {VEHICLES.map((v) => (
                      <div
                        key={v.key}
                        className={`bs-vehicle-card${form.vehicle === v.key ? " selected" : ""}`}
                        onClick={() => {
                          setForm((prev) => ({ ...prev, vehicle: v.key }));
                          setErrors({});
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setForm((prev) => ({ ...prev, vehicle: v.key }));
                            setErrors({});
                          }
                        }}
                      >
                        <img
                          src={v.img}
                          srcSet={`${v.img.replace(/\.webp$/, "-420.webp")} 420w, ${v.img} 840w`}
                          sizes="(min-width: 768px) 186px, calc((100vw - 44px) / 3)"
                          alt={`GS Limo Elite ${v.label} — ${v.capacity}`}
                          title={`GS Limo Elite ${v.label} — ${v.capacity}`}
                          className="bs-vehicle-img"
                          width="840"
                          height="469"
                          loading="lazy"
                        />
                        <div className="bs-vehicle-info">
                          <strong>{v.label}</strong>
                          <span>
                            <i className="fas fa-user-friends"></i> {v.capacity}
                          </span>
                        </div>
                        {form.vehicle === v.key && (
                          <div className="bs-vehicle-check">
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-sm-6">
                      <label className="gs-label" htmlFor="booking-luggage">
                        <i className="fas fa-suitcase me-1 text-gold-dim"></i>
                        Checked Bags
                      </label>
                      <select
                        id="booking-luggage"
                        name="luggage"
                        value={form.luggage}
                        onChange={handleChange}
                        className="gs-input"
                      >
                        <option value="">Select bags...</option>
                        {LUGGAGE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n === "0" ? "Carry-on only" : n}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-6">
                      <label className="gs-label" htmlFor="booking-stops">
                        <i className="fas fa-map-pin me-1 text-gold-dim"></i>
                        Extra Stops
                      </label>
                      <select
                        id="booking-stops"
                        name="stops"
                        value={form.stops}
                        onChange={handleChange}
                        className="gs-input"
                      >
                        <option value="">Select stops...</option>
                        {STOP_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n === "0" ? "None" : n}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {errors.vehicle && (
                    <span className="gs-err mt-2 d-block text-center">
                      {errors.vehicle}
                    </span>
                  )}
                  <div className="car-seat-notice mt-3">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span className="car-seat-notice-text">
                      <strong>Car Seat Notice: </strong>
                      GS Limo Elite does not provide child car seats. Passengers
                      must bring and install their own.
                    </span>
                  </div>
                  <div className="d-flex gap-2 mt-4">
                    <button
                      type="button"
                      className="btn gs-btn-outline-gold bs-btn-back"
                      onClick={goBack}
                    >
                      <i className="fas fa-arrow-left me-2"></i>Back
                    </button>
                    <button
                      type="button"
                      className="btn gs-btn-gold flex-grow-1 bs-btn"
                      onClick={goNext}
                    >
                      Next: Details
                      <i className="fas fa-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* ── Step 3: Details ── */}
              {step === 3 && (
                <div className="bs-panel">
                  <h3 className="booking-form-title">
                    <i className="fas fa-user me-2"></i>Passenger Details
                  </h3>

                  <div className="bs-summary">
                    <div className="bs-summary-head">
                      <i className="fas fa-clipboard-check"></i>
                      Trip Summary
                    </div>
                    <div className="bs-summary-row">
                      <span>Trip</span>
                      <strong>{form.tripType}</strong>
                    </div>
                    <div className="bs-summary-row">
                      <span>From</span>
                      <strong>{form.pickup || "—"}</strong>
                    </div>
                    {form.tripType !== "Hourly" && (
                      <div className="bs-summary-row">
                        <span>To</span>
                        <strong>{form.dropoff || "—"}</strong>
                      </div>
                    )}
                    <div className="bs-summary-row">
                      <span>Pickup</span>
                      <strong>
                        {summaryDate}
                        {form.time ? ` · ${formatTime(form.time)}` : ""}
                      </strong>
                    </div>
                    {form.tripType === "Round-trip" && (
                      <div className="bs-summary-row">
                        <span>Return</span>
                        <strong>
                          {formatDate(form.returnDate)}
                          {form.returnTime
                            ? ` · ${formatTime(form.returnTime)}`
                            : ""}
                        </strong>
                      </div>
                    )}
                    {form.tripType === "Hourly" && (
                      <div className="bs-summary-row">
                        <span>Duration</span>
                        <strong>{form.hours}</strong>
                      </div>
                    )}
                    <div className="bs-summary-row">
                      <span>Vehicle</span>
                      <strong>{selectedVehicle?.label ?? "—"}</strong>
                    </div>
                    <div className="bs-summary-row">
                      <span>Passengers</span>
                      <strong>{form.passengers}</strong>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} noValidate>
                    <div className="row g-3">
                      <div className="col-sm-6">
                        <label className="gs-label" htmlFor="booking-name">
                          Full Name *
                        </label>
                        <input
                          id="booking-name"
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          className={`gs-input${errors.name ? " gs-input-err" : ""}`}
                          placeholder="Your full name"
                        />
                        {errors.name && (
                          <span className="gs-err">{errors.name}</span>
                        )}
                      </div>
                      <div className="col-sm-6">
                        <label className="gs-label" htmlFor="booking-phone">
                          Phone Number *
                        </label>
                        <input
                          id="booking-phone"
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          className={`gs-input${errors.phone ? " gs-input-err" : ""}`}
                          placeholder="(000) 000-0000"
                        />
                        {errors.phone && (
                          <span className="gs-err">{errors.phone}</span>
                        )}
                      </div>
                      <div className="col-12">
                        <label className="gs-label" htmlFor="booking-email">
                          Email Address *
                        </label>
                        <input
                          id="booking-email"
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          className={`gs-input${errors.email ? " gs-input-err" : ""}`}
                          placeholder="your@email.com"
                        />
                        {errors.email && (
                          <span className="gs-err">{errors.email}</span>
                        )}
                      </div>
                      <div className="col-sm-6">
                        <label className="gs-label" htmlFor="booking-service">
                          Service Type
                        </label>
                        <select
                          id="booking-service"
                          name="service"
                          value={form.service}
                          onChange={handleChange}
                          className="gs-input"
                        >
                          <option value="">Select service...</option>
                          {SERVICE_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label className="gs-label" htmlFor="booking-passengers">
                          Passengers
                        </label>
                        <select
                          id="booking-passengers"
                          name="passengers"
                          value={form.passengers}
                          onChange={handleChange}
                          className="gs-input"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(
                            (n) => (
                              <option key={n} value={n}>
                                {n} {n === 1 ? "Passenger" : "Passengers"}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="gs-label" htmlFor="booking-notes">
                          Special Requests
                        </label>
                        <textarea
                          id="booking-notes"
                          name="notes"
                          value={form.notes}
                          onChange={handleChange}
                          className="gs-input"
                          rows={3}
                          placeholder="Child seat, accessibility needs, special instructions..."
                        ></textarea>
                      </div>
                    </div>
                    <div className="d-flex gap-2 mt-4">
                      <button
                        type="button"
                        className="btn gs-btn-outline-gold bs-btn-back"
                        onClick={goBack}
                      >
                        <i className="fas fa-arrow-left me-2"></i>Back
                      </button>
                      <button
                        type="submit"
                        className="btn gs-btn-gold flex-grow-1 bs-btn"
                        disabled={status === "sending"}
                      >
                        {status === "sending" ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="far fa-calendar-check me-2"></i>Send
                            Request
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
